/**
 * Admin Invitations Management Page — moderation, search, filtering, bulk
 * actions, lifecycle history, and a stats strip across the top.
 *
 * The page is a table (one row per invitation) rather than the user-facing
 * cards layout because admins need to scan many rows quickly. Bulk actions
 * are exposed via per-row checkboxes + a sticky action bar.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { cn } from '../../infrastructure/lib/utils';
import { useFormat } from '@/src/infrastructure/lib/format';
import { InvitationRow } from './InvitationRow';
import {
  archiveInvitation,
  bulkInvitationAction,
  getInvitationHistory,
  getInvitationStats,
  listInvitations,
  restoreInvitation,
  suspendInvitation,
  type AdminBulkAction,
  type AdminInvitation,
  type AdminInvitationHistoryEntry,
  type AdminInvitationStats,
} from '../../infrastructure/lib/api';

const TABS: Array<{ id: string; label: string; status?: string }> = [
  { id: 'all', label: 'All' },
  { id: 'PUBLISHED', label: 'Active', status: 'PUBLISHED' },
  { id: 'EXPIRED', label: 'Expired', status: 'EXPIRED' },
  { id: 'ARCHIVED', label: 'Archived', status: 'ARCHIVED' },
  { id: 'CANCELLED', label: 'Cancelled', status: 'CANCELLED' },
  { id: 'SUSPENDED', label: 'Suspended', status: 'SUSPENDED' },
  { id: 'REPORTED', label: 'Reported', status: 'REPORTED' },
];

const SORTS: Array<{ value: string; label: string }> = [
  { value: 'createdAt', label: 'Newest' },
  { value: 'updatedAt', label: 'Recently updated' },
  { value: 'eventDate', label: 'Event date' },
  { value: 'expiresAt', label: 'Expiry' },
  { value: 'totalViews', label: 'Most viewed' },
  { value: 'totalRsvps', label: 'Most RSVPs' },
  { value: 'reportedCount', label: 'Most reported' },
];

// STATUS_TONE moved to InvitationRow.tsx

/**
 * Module-level fallback — used by `exportCsv` (a pure function called
 * outside the component). For UI cells, prefer the `fmt.date` helper from
 * `useFormat()` inside the component so the user's date-format preference
 * takes effect.
 */
function formatDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function exportCsv(rows: AdminInvitation[]) {
  const header = [
    'id',
    'eventName',
    'occasionType',
    'status',
    'owner',
    'ownerEmail',
    'eventDate',
    'expiresAt',
    'views',
    'rsvps',
    'shares',
    'reports',
    'createdAt',
  ];
  const lines = rows.map((r) =>
    [
      r.id,
      JSON.stringify(r.eventName ?? ''),
      r.occasionType,
      r.status,
      JSON.stringify(r.user?.name ?? ''),
      r.user?.email ?? '',
      r.eventDate,
      r.expiresAt ?? '',
      r.totalViews ?? 0,
      r.totalRsvps ?? 0,
      r.totalShares ?? 0,
      r.reportedCount ?? 0,
      r.createdAt,
    ].join(','),
  );
  const blob = new Blob([[header.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invitations-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function InvitationsPage() {
  const fmt = useFormat();
  const formatDateCell = useCallback((iso?: string | null) => (iso ? fmt.date(iso) : '—'), [fmt]);
  const [tab, setTab] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminInvitation[]>([]);
  const [meta, setMeta] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null);
  const [stats, setStats] = useState<AdminInvitationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [historyTarget, setHistoryTarget] = useState<AdminInvitation | null>(null);
  const [history, setHistory] = useState<AdminInvitationHistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const status = useMemo(() => TABS.find((t) => t.id === tab)?.status, [tab]);

  // Debounced fetch when search changes; immediate on tab/page/sort/refresh.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listInvitations({
          page,
          limit: 20,
          search: search.trim() || undefined,
          status,
          sortBy,
          sortOrder,
        });
        if (cancelled) return;
        setItems(res.items);
        setMeta(res.meta);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load invitations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const debounce = setTimeout(run, search ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [page, search, status, sortBy, sortOrder, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    getInvitationStats()
      .then((s) => !cancelled && setStats(s))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((i) => i.id)),
    );
  }, [items]);

  const onRowAction = useCallback(
    async (id: string, action: 'archive' | 'suspend' | 'restore') => {
      setBusy(true);
      try {
        if (action === 'archive') await archiveInvitation(id);
        else if (action === 'suspend') {
          const reason = window.prompt('Reason for suspension?') ?? undefined;
          await suspendInvitation(id, reason || undefined);
        } else await restoreInvitation(id);
        setRefreshKey((k) => k + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Action failed');
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const onBulkAction = useCallback(
    async (action: AdminBulkAction) => {
      if (selected.size === 0) return;
      setBusy(true);
      try {
        const reason =
          action === 'suspend' ? window.prompt('Reason for bulk suspension?') ?? undefined : undefined;
        await bulkInvitationAction([...selected], action, reason || undefined);
        setSelected(new Set());
        setRefreshKey((k) => k + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Bulk action failed');
      } finally {
        setBusy(false);
      }
    },
    [selected],
  );

  const openHistory = useCallback(async (inv: AdminInvitation) => {
    setHistoryTarget(inv);
    setHistory(null);
    setHistoryLoading(true);
    try {
      const entries = await getInvitationHistory(inv.id);
      setHistory(entries);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h2 className="font-display text-4xl font-bold">Invitation Management</h2>
        <p className="text-on-surface-variant text-sm">
          Platform-wide moderation, lifecycle, and analytics for every invitation.
        </p>
      </header>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total', value: stats?.total, tone: 'text-electric-violet' },
          { label: 'Active', value: stats?.byStatus?.PUBLISHED, tone: 'text-emerald-400' },
          { label: 'Expired', value: stats?.byStatus?.EXPIRED, tone: 'text-zinc-300' },
          { label: 'Archived', value: stats?.byStatus?.ARCHIVED, tone: 'text-amber-300' },
          { label: 'Suspended', value: stats?.suspended, tone: 'text-red-300' },
          { label: 'Reported', value: stats?.reported, tone: 'text-orange-300' },
        ].map((card) => (
          <div
            key={card.label}
            className="glass-panel rounded-xl px-4 py-3 flex flex-col gap-1"
          >
            <span className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
              {card.label}
            </span>
            <span className={cn('text-2xl font-display font-bold tabular-nums', card.tone)}>
              {card.value ?? '—'}
            </span>
          </div>
        ))}
      </div>

      {/* Tabs + filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 border-b border-border-subtle pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setPage(1);
                setSelected(new Set());
              }}
              className={cn(
                'px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all',
                tab === t.id
                  ? 'bg-electric-violet text-white'
                  : 'text-on-surface-variant hover:bg-white/5',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <input
            type="text"
            placeholder="Search by event name, slug, or owner email…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="flex-1 bg-surface-container-lowest border border-border-subtle rounded-xl px-4 py-2.5 text-sm placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-surface-container-lowest border border-border-subtle rounded-xl px-3 py-2.5 text-sm"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
            className="bg-surface-container-lowest border border-border-subtle rounded-xl px-3 py-2.5 text-sm"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
          <button
            type="button"
            onClick={() => exportCsv(items)}
            disabled={items.length === 0}
            className="bg-surface-container-lowest border border-border-subtle rounded-xl px-3 py-2.5 text-sm font-bold disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-2 z-10 glass-panel rounded-xl px-4 py-3 flex items-center justify-between"
        >
          <span className="text-sm font-bold">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void onBulkAction('archive')}
              className="px-3 py-1.5 text-xs font-bold uppercase rounded-full bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
            >
              Archive
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onBulkAction('suspend')}
              className="px-3 py-1.5 text-xs font-bold uppercase rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30"
            >
              Suspend
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onBulkAction('restore')}
              className="px-3 py-1.5 text-xs font-bold uppercase rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
            >
              Restore
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-xs font-bold uppercase rounded-full text-on-surface-variant hover:bg-white/5"
            >
              Clear
            </button>
          </div>
        </motion.div>
      )}

      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="glass-panel rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-[10px] uppercase tracking-widest text-on-surface-variant font-bold">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-3 py-3 text-left">Event</th>
                <th className="px-3 py-3 text-left">Owner</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-left">Event Date</th>
                <th className="px-3 py-3 text-left">Expiry</th>
                <th className="px-3 py-3 text-right">Views</th>
                <th className="px-3 py-3 text-right">RSVPs</th>
                <th className="px-3 py-3 text-right">Reports</th>
                <th className="px-3 py-3 text-right">Updated</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    <td colSpan={11} className="px-3 py-4">
                      <div className="h-6 rounded bg-white/5 animate-pulse" />
                    </td>
                  </tr>
                ))}
              {!loading &&
                items.map((inv) => (
                  <InvitationRow
                    key={inv.id}
                    inv={inv}
                    isChecked={selected.has(inv.id)}
                    busy={busy}
                    toggleSelect={toggleSelect}
                    openHistory={openHistory}
                    onRowAction={onRowAction}
                    formatDateCell={formatDateCell}
                  />
                ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-12 text-center text-on-surface-variant">
                    No invitations match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm text-on-surface-variant">
          <button
            type="button"
            disabled={meta.page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-full border border-border-subtle disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4 inline" /> Prev
          </button>
          <span className="font-bold">
            Page {meta.page} of {meta.totalPages}
          </span>
          <button
            type="button"
            disabled={meta.page >= meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-full border border-border-subtle disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      {/* History modal */}
      {historyTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden">
            <header className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
                  Lifecycle history
                </p>
                <h3 className="font-display text-lg font-bold line-clamp-1">
                  {historyTarget.eventName}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setHistoryTarget(null)}
                className="p-1.5 rounded hover:bg-white/5 text-on-surface-variant"
              >
                <X className="w-5 h-5" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-5">
              {historyLoading && (
                <div className="py-10 flex justify-center text-on-surface-variant">Loading…</div>
              )}
              {!historyLoading && history && history.length === 0 && (
                <div className="py-10 text-center text-on-surface-variant text-sm">
                  No events recorded.
                </div>
              )}
              {!historyLoading && history && history.length > 0 && (
                <ol className="space-y-3">
                  {history.map((entry) => (
                    <li
                      key={entry.id}
                      className="rounded-lg border border-border-subtle bg-surface-container-lowest p-3"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs font-bold uppercase tracking-widest text-electric-violet">
                          {entry.action}
                        </span>
                        <time className="text-[11px] text-on-surface-variant tabular-nums">
                          {fmt.dateTime(entry.createdAt)}
                        </time>
                      </div>
                      {Object.keys(entry.metadata).length > 0 && (
                        <p className="text-xs text-on-surface-variant mt-1 break-words">
                          {JSON.stringify(entry.metadata)}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
